/**
 * Badge Component
 */

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@tonnie/foundation';

export const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary/10 text-primary-accessible',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        success: 'border-transparent bg-tone-green-subtle text-tone-green',
        warning: 'border-transparent bg-tone-amber-subtle text-tone-amber',
        destructive: 'border-transparent bg-destructive/10 text-destructive-accessible',
        outline: 'border-border text-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  /**
   * Render the badge's look on its single child — a badge that DOES something
   * is `<Badge asChild><button …>`, never a `<Badge onClick>` a keyboard cannot
   * reach (lint: BADGE_NOT_A_CONTROL).
   */
  asChild?: boolean;
}

export function Badge({ className, variant, asChild = false, ...props }: BadgeProps) {
  const Comp = asChild ? Slot : 'div';
  return <Comp className={cn(badgeVariants({ variant }), className)} {...props} />;
}
